/**
 * HMS To-Do List Component
 * 
 * Corporate-ready todo management for HMS users.
 * Full CRUD operations with priority, categories, and due dates.
 * 
 * Features:
 * - Create, read, update, delete todos
 * - Mark as complete/pending
 * - Priority levels (low, normal, urgent)
 * - Categories
 * - Due dates with overdue indicators
 * - Filters and search
 * - Statistics dashboard
 * - Responsive design
 */

import React, { useState, useEffect, useContext, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import todoService from '../services/todoService';
import Footer from './ui/Footer';
import Layout from './Layout';
import TodoDatePicker from './ui/TodoDatePicker';
import { useFlashMessage } from './FlashMessage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faListCheck,
  faPlus,
  faCheckCircle,
  faEdit,
  faTrash,
  faCircle,
  faCalendar,
  faSave,
  faTimes,
  faExclamationTriangle,
  faPlay,
  faClock
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../utils/axiosConfig';
import { getAbsoluteMediaUrl } from '../utils/mediaUtils';
import HMSPageHeader from './ui/HMSPageHeader';
import HMSBadge from './ui/HMSBadge';
import HMSButton from './ui/HMSButton';
import HamsterFloatingButton from './HamsterFloatingButton';
import BackToTopButton from './BackToTopButton';
import './HMSTodoList.css';
import './HMSTodoList-HighlightExtension.css';

function HMSTodoList() {
  const { user } = useContext(AuthContext);
  const { showSuccess, showError, showInfo } = useFlashMessage();
  const location = useLocation();
  const todoRefs = useRef({});
  
  // State
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [currentUserAvatar, setCurrentUserAvatar] = useState(null);
  const [currentUserData, setCurrentUserData] = useState(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'pending', 'in_progress', 'completed'
  
  // Expanded todos state (for description expansion)
  const [expandedTodos, setExpandedTodos] = useState(new Set());
  
  // Highlighted todo state (for navigation from notifications)
  const [highlightedTodoId, setHighlightedTodoId] = useState(null);
  
  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [todoToDelete, setTodoToDelete] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    priority: 'all'
  });

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [editingTodo, setEditingTodo] = useState(null);
  const [todoForm, setTodoForm] = useState({
    title: '',
    description: '',
    priority: 'normal',
    due_date: ''
  });

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch current user avatar
  useEffect(() => {
    const fetchCurrentUserAvatar = async () => {
      try {
        const profileResponse = await axiosInstance.get('/api/profile/me/');
        console.log('🖼️ Full Profile Response:', profileResponse.data);
        
        // API returns data nested in success/data structure
        const profileData = profileResponse.data.data || profileResponse.data;
        console.log('🖼️ Profile Data:', profileData);
        console.log('🖼️ Avatar Photo Field:', profileData.avatar_photo);
        
        if (profileData.avatar_photo) {
          const absoluteAvatarUrl = getAbsoluteMediaUrl(profileData.avatar_photo);
          console.log('🖼️ Absolute Avatar URL:', absoluteAvatarUrl);
          setCurrentUserAvatar(absoluteAvatarUrl);
        }
        setCurrentUserData(profileData);
      } catch (error) {
        console.error('Error fetching user avatar:', error);
      }
    };

    fetchCurrentUserAvatar();
  }, []);

  // Fetch data on component mount and filter changes
  useEffect(() => {
    fetchTodos();
    fetchStats();
    fetchCategories();
  }, [filters, activeTab]);

  // Track pending highlight ID from navigation
  const pendingHighlightId = useRef(null);

  // Capture highlight ID from location state (only runs when location.state changes)
  useEffect(() => {
    const highlightTodoId = location.state?.highlightTodoId;
    if (highlightTodoId) {
      pendingHighlightId.current = highlightTodoId;
      // Clear location state immediately to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Process highlight when todos are loaded (only runs when pendingHighlightId is set)
  useEffect(() => {
    if (pendingHighlightId.current && todos.length > 0) {
      const highlightId = pendingHighlightId.current;
      const todoToHighlight = todos.find(t => t.id === highlightId);
      
      // Clear pending highlight immediately to prevent re-processing (even if todo not found)
      pendingHighlightId.current = null;
      
      if (todoToHighlight) {
        // Set highlighted state
        setHighlightedTodoId(highlightId);
        
        // Switch to the appropriate tab based on todo status
        if (todoToHighlight.status === 'pending') {
          setActiveTab('pending');
        } else if (todoToHighlight.status === 'in_progress') {
          setActiveTab('in_progress');
        } else if (todoToHighlight.status === 'completed') {
          setActiveTab('completed');
        } else {
          setActiveTab('all');
        }
        
        // Scroll to the todo after a short delay (to allow rendering)
        setTimeout(() => {
          const todoElement = todoRefs.current[highlightId];
          if (todoElement) {
            todoElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 300);
        
        // Remove highlight after 3 seconds
        setTimeout(() => {
          setHighlightedTodoId(null);
        }, 3000);
      }
    }
  }, [todos]);

  /**
   * Fetch todos from API
   */
  const fetchTodos = async () => {
    try {
      setLoading(true);
      
      // Apply tab-specific filtering
      const tabFilters = { ...filters };
      if (activeTab !== 'all') {
        tabFilters.status = activeTab;
      }
      
      const data = await todoService.getTodos(tabFilters);
      setTodos(data);
    } catch (err) {
      console.error('Error fetching todos:', err);
      showError('Failed to load todos. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch todo statistics
   */
  const fetchStats = async () => {
    try {
      const statsData = await todoService.getStats();
      setStats(statsData);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  /**
   * Fetch user's categories
   */
  const fetchCategories = async () => {
    try {
      const categoriesData = await todoService.getCategories();
      setCategories(categoriesData);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  /**
   * Handle opening create modal
   */
  const handleOpenCreateModal = () => {
    setEditingTodo(null);
    setTodoForm({
      title: '',
      description: '',
      priority: 'normal',
      due_date: ''
    });
    setShowModal(true);
  };

  /**
   * Handle opening edit modal
   */
  const handleOpenEditModal = (todo) => {
    setEditingTodo(todo);
    setTodoForm({
      title: todo.title,
      description: todo.description || '',
      priority: todo.priority,
      due_date: todo.due_date || ''
    });
    setShowModal(true);
  };

  /**
   * Handle closing modal
   */
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTodo(null);
    setTodoForm({
      title: '',
      description: '',
      priority: 'normal',
      due_date: ''
    });
  };

  /**
   * Handle form input change
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTodoForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  /**
   * Handle form submit (create or update)
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!todoForm.title.trim()) {
      showError('Title is required');
      return;
    }

    try {
      // Prepare data - convert empty due_date to null
      const submitData = {
        ...todoForm,
        due_date: todoForm.due_date || null
      };

      if (editingTodo) {
        // Update existing todo
        await todoService.updateTodo(editingTodo.id, submitData);
        showSuccess('Todo updated successfully');
      } else {
        // Create new todo
        await todoService.createTodo(submitData);
        showSuccess('Todo created successfully');
      }
      
      handleCloseModal();
      fetchTodos();
      fetchStats();
      fetchCategories();
    } catch (err) {
      console.error('Error saving todo:', err);
      showError('Failed to save todo. Please try again.');
    }
  };

  /**
   * Handle toggling todo completion
   */
  const handleToggleComplete = async (todo) => {
    try {
      const newStatus = todo.status === 'completed' ? 'pending' : 'completed';
      await todoService.toggleComplete(todo.id, todo.status);
      
      // Update local state - remove if doesn't match active tab filter
      setTodos(prevTodos => {
        if (activeTab !== 'all' && activeTab !== newStatus) {
          // Remove from list if status doesn't match current tab
          return prevTodos.filter(t => t.id !== todo.id);
        }
        // Update status if staying in view
        return prevTodos.map(t => 
          t.id === todo.id ? { ...t, status: newStatus } : t
        );
      });
      
      // Update stats
      fetchStats();
    } catch (err) {
      console.error('Error toggling todo:', err);
      showError('Failed to update todo status');
      // Revert on error
      fetchTodos();
    }
  };

  /**
   * Handle marking todo as in progress
   */
  const handleMarkInProgress = async (todoId) => {
    try {
      await todoService.markInProgress(todoId);
      
      // Update local state - remove if doesn't match active tab filter
      setTodos(prevTodos => {
        if (activeTab !== 'all' && activeTab !== 'in_progress') {
          // Remove from list if status doesn't match current tab
          return prevTodos.filter(t => t.id !== todoId);
        }
        // Update status if staying in view
        return prevTodos.map(t => 
          t.id === todoId ? { ...t, status: 'in_progress' } : t
        );
      });
      
      // Update stats
      fetchStats();
    } catch (err) {
      console.error('Error marking todo in progress:', err);
      showError('Failed to update todo status');
      // Revert on error
      fetchTodos();
    }
  };

  /**
   * Handle marking todo as pending
   */
  const handleMarkPending = async (todoId) => {
    try {
      await todoService.updateTodo(todoId, { status: 'pending' });
      
      // Update local state - remove if doesn't match active tab filter
      setTodos(prevTodos => {
        if (activeTab !== 'all' && activeTab !== 'pending') {
          // Remove from list if status doesn't match current tab
          return prevTodos.filter(t => t.id !== todoId);
        }
        // Update status if staying in view
        return prevTodos.map(t => 
          t.id === todoId ? { ...t, status: 'pending' } : t
        );
      });
      
      // Update stats
      fetchStats();
    } catch (err) {
      console.error('Error marking todo as pending:', err);
      showError('Failed to update todo status');
      // Revert on error
      fetchTodos();
    }
  };

  /**
   * Open delete confirmation modal
   */
  const handleDeleteClick = (todo) => {
    setTodoToDelete(todo);
    setDeleteModalOpen(true);
  };

  /**
   * Close delete modal
   */
  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setTodoToDelete(null);
  };

  /**
   * Confirm and delete todo
   */
  const handleConfirmDelete = async () => {
    if (!todoToDelete) return;

    try {
      await todoService.deleteTodo(todoToDelete.id);
      showSuccess('Todo deleted successfully');
      
      // Update local state immediately
      setTodos(prevTodos => prevTodos.filter(t => t.id !== todoToDelete.id));
      
      // Update stats and categories
      fetchStats();
      fetchCategories();
      
      // Close modal
      handleCloseDeleteModal();
    } catch (err) {
      console.error('Error deleting todo:', err);
      showError('Failed to delete todo');
      // Revert on error
      fetchTodos();
      handleCloseDeleteModal();
    }
  };

  /**
   * Handle filter change
   */
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  /**
   * Toggle todo description expansion
   */
  const toggleTodoExpansion = (todoId) => {
    setExpandedTodos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(todoId)) {
        newSet.delete(todoId);
      } else {
        newSet.add(todoId);
      }
      return newSet;
    });
  };

  /**
   * Get todos (no client-side filtering needed)
   */
  const getFilteredTodos = () => {
    return todos;
  };

  /**
   * Get the tone props for a priority chip
   *
   * HMSBadge carries two priority tones and this screen has three. `high` is
   * the primitive's red and `normal` its blue; only `low` has no equivalent,
   * so it keeps a screen tone class on the shared chip — the same call
   * HMSNotifications made for `.notif-priority-low`.
   *
   * The fallback is deliberately unchanged: anything unrecognised takes the
   * blue, which is what the old lookup did by returning `todo-priority-normal`
   * for a miss. `urgent` — the create form's top option — is one of those
   * misses and still renders blue. Repointing it at the red is a data-side
   * behaviour change no screenshot can catch, so it is not this pass's.
   */
  const getPriorityProps = (priority) => {
    if (priority === 'low') {
      return { className: 'todo-priority-low' };
    }
    return { priority: priority === 'high' ? 'high' : 'normal' };
  };

  /**
   * Get status icon
   */
  const getStatusIcon = (status) => {
    return status === 'completed' ? faCheckCircle : faCircle;
  };

  /**
   * Check if todo is overdue
   */
  const isOverdue = (todo) => {
    if (!todo.due_date || todo.status === 'completed') {
      return false;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = new Date(todo.due_date);
    return dueDate < today;
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredTodos = getFilteredTodos();

  const todoTabs = [
    { key: 'all', label: 'All Todos', icon: faListCheck, count: stats ? stats.pending + stats.in_progress + stats.completed : 0, showZero: true },
    { key: 'pending', label: 'Pending', icon: faClock, count: stats ? stats.pending : 0, showZero: true },
    { key: 'in_progress', label: 'In Progress', icon: faPlay, count: stats ? stats.in_progress : 0, showZero: true },
    { key: 'completed', label: 'Completed', icon: faCheckCircle, count: stats ? stats.completed : 0, showZero: true },
  ];

  return (
    <Layout showLeftNavSidebar={true} showRightSidebar={false}>
      <div className="todolist-container">
        {/* Shared screen header: brand block, pill tray, stats + create + avatar cluster */}
        <HMSPageHeader
          title="To-Do List"
          kicker="Workspace"
          tabs={{ items: todoTabs, activeKey: activeTab, onChange: setActiveTab }}
          actions={
            <>
              {stats && (
                <div className="header-stats-compact">
                  <div className="stat-card-compact stat-pending">
                    <div className="stat-value-compact">{stats.pending}</div>
                    <div className="stat-label-compact">Pending</div>
                  </div>
                  <div className="stat-card-compact stat-progress">
                    <div className="stat-value-compact">{stats.in_progress}</div>
                    <div className="stat-label-compact">Progress</div>
                  </div>
                  <div className="stat-card-compact stat-completed">
                    <div className="stat-value-compact">{stats.completed}</div>
                    <div className="stat-label-compact">Done</div>
                  </div>
                </div>
              )}

              <button
                className="create-todo-btn-header"
                onClick={handleOpenCreateModal}
              >
                <FontAwesomeIcon icon={faPlus} />
                Create Todo
              </button>

              <div className="header-user-avatar">
                {currentUserAvatar && (
                  <img
                    src={currentUserAvatar}
                    alt="Your profile"
                    className="user-avatar-image"
                    onError={(e) => {
                      console.error('Failed to load avatar image:', currentUserAvatar);
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                )}
                <div
                  className="user-avatar-initials"
                  style={{ display: currentUserAvatar ? 'none' : 'flex' }}
                >
                  {currentUserData?.first_name?.[0] || ''}{currentUserData?.last_name?.[0] || ''}
                </div>
              </div>
            </>
          }
        />

       {/* Compact Filters - Right Aligned */}
      <div className="filters-search-compact-container">
        <div className="filters-compact-group">
          <select
            value={filters.priority}
            onChange={(e) => handleFilterChange('priority', e.target.value)}
            className="filter-select-compact"
          >
            <option value="all">All Priorities</option>
            <option value="urgent">Urgent</option>
            <option value="normal">Normal</option>
            <option value="low">Low</option>
          </select>
        </div>
      </div>

      {/* Todo List Content */}
      <div className="todolist-content">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading todos...</p>
          </div>
        ) : filteredTodos.length === 0 ? (
          <div className="empty-state">
            <FontAwesomeIcon icon={faListCheck} size="3x" />
            <h3>No todos found</h3>
            <p>Create your first todo to get started!</p>
          </div>
        ) : (
          <div className="todos-grid">
            {filteredTodos.map(todo => (
              <div 
                key={todo.id}
                ref={el => todoRefs.current[todo.id] = el}
                className={`todo-card ${todo.status} ${isOverdue(todo) ? 'overdue' : ''} ${highlightedTodoId === todo.id ? 'hms-todo-card-highlighted' : ''}`}
              >
                {/* Title and Description Section */}
                <div 
                  className="todo-info-section"
                  onClick={() => todo.description && toggleTodoExpansion(todo.id)}
                  style={{ cursor: todo.description ? 'pointer' : 'default' }}
                >
                  <h3 className="todo-title">{todo.title}</h3>
                  {todo.description && (
                    <p className={`todo-description ${expandedTodos.has(todo.id) ? 'expanded' : ''}`}>
                      {todo.description}
                    </p>
                  )}
                </div>

                {/* Meta Information */}
                <div className="todo-meta-section">
                  {todo.due_date ? (
                    <div className="todo-due-date">
                      <FontAwesomeIcon icon={faClock} />
                      <span className={isOverdue(todo) ? 'overdue-text' : 'due-date-text'}>
                        {isOverdue(todo) ? 'Expired:' : 'Expires:'} {formatDate(todo.due_date)}
                      </span>
                    </div>
                  ) : (
                    <div className="todo-no-due-date">
                      <span>No due date</span>
                    </div>
                  )}
                  <HMSBadge {...getPriorityProps(todo.priority)}>
                    {todo.priority_display}
                  </HMSBadge>
                </div>

                {/* Status Buttons */}
                <div className="todo-status-section">
                  <button
                    onClick={() => handleMarkPending(todo.id)}
                    className={`status-btn status-btn-pending ${todo.status === 'pending' ? 'active' : ''}`}
                    title="Mark as Pending"
                  >
                    <FontAwesomeIcon icon={faClock} />
                    <span>Pending</span>
                  </button>
                  <button
                    onClick={() => handleMarkInProgress(todo.id)}
                    className={`status-btn status-btn-progress ${todo.status === 'in_progress' ? 'active' : ''}`}
                    title="Mark as In Progress"
                  >
                    <FontAwesomeIcon icon={faPlay} />
                    <span>In Progress</span>
                  </button>
                  <button
                    onClick={() => handleToggleComplete(todo)}
                    className={`status-btn status-btn-complete ${todo.status === 'completed' ? 'active' : ''}`}
                    title="Mark as Complete"
                  >
                    <FontAwesomeIcon icon={faCheckCircle} />
                    <span>Complete</span>
                  </button>
                </div>

                {/* Action Buttons */}
                <div className="todo-action-section">
                  <HMSButton
                    variant="icon"
                    onClick={() => handleOpenEditModal(todo)}
                    className="todo-action-btn todo-action-btn-edit"
                    title="Edit"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </HMSButton>
                  <HMSButton
                    variant="icon"
                    onClick={() => handleDeleteClick(todo)}
                    className="todo-action-btn todo-action-btn-delete"
                    title="Delete"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </HMSButton>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="todo-modal-overlay" onClick={handleCloseModal}>
          <div className="todo-modal" onClick={(e) => e.stopPropagation()}>
            <div className="todo-modal-header">
              <h2>{editingTodo ? 'Edit Todo' : 'Create New Todo'}</h2>
              <button className="modal-close-btn" onClick={handleCloseModal}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="todo-form">
              <div className="form-group">
                <label htmlFor="title">Title *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={todoForm.title}
                  onChange={handleInputChange}
                  placeholder="Enter todo title"
                  required
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={todoForm.description}
                  onChange={handleInputChange}
                  placeholder="Enter description (optional)"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label htmlFor="priority">Priority</label>
                <select
                  id="priority"
                  name="priority"
                  value={todoForm.priority}
                  onChange={handleInputChange}
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              <div className="form-group">
                <TodoDatePicker
                  id="due_date"
                  name="due_date"
                  value={todoForm.due_date}
                  onChange={handleInputChange}
                  placeholder="Select due date (optional)"
                  label="Due Date (Optional)"
                  required={false}
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={handleCloseModal}>
                  <FontAwesomeIcon icon={faTimes} />
                  Cancel
                </button>
                <button type="submit" className="btn-save">
                  <FontAwesomeIcon icon={faSave} />
                  {editingTodo ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && todoToDelete && (
        <div className="hms-todo-delete-modal-overlay" onClick={handleCloseDeleteModal}>
          <div className="hms-todo-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="hms-todo-delete-modal-icon">
              <FontAwesomeIcon icon={faExclamationTriangle} />
            </div>
            <h2 className="hms-todo-delete-modal-title">Delete Todo?</h2>
            <p className="hms-todo-delete-modal-message">
              Are you sure you want to delete "<strong>{todoToDelete.title}</strong>"? 
              This action cannot be undone.
            </p>
            <div className="hms-todo-delete-modal-actions">
              <button 
                className="hms-todo-delete-modal-btn hms-todo-delete-modal-cancel" 
                onClick={handleCloseDeleteModal}
              >
                Cancel
              </button>
              <button 
                className="hms-todo-delete-modal-btn hms-todo-delete-modal-confirm" 
                onClick={handleConfirmDelete}
              >
                <FontAwesomeIcon icon={faTrash} />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

        {/* Hamster Chat Integration */}
        <HamsterFloatingButton />

        {/* Back to Top Button */}
        <BackToTopButton />

        <Footer />
      </div>
    </Layout>
  );
}

export default HMSTodoList;
